import { useEffect, useRef } from "react";

const headers = new Map();

export const visibleHeaderOffset = (varName) => {
  const header = headers.get(varName);
  return header?.isConnected ? Math.max(0, header.getBoundingClientRect().bottom)
    : parseFloat(getComputedStyle(document.documentElement).getPropertyValue(varName)) || 0;
};

/**
 * Measures a sticky header's rendered height and publishes it as a CSS
 * variable on the document root, so sticky content further down the page
 * (filter/sort bars) can pin itself right below the header instead of
 * overlapping it — works at any viewport width without hardcoded offsets.
 */
export function useHeaderHeightVar(varName = "--sticky-header-h") {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    headers.set(varName, el);
    let frame = null;
    let previous = null;
    let disposed = false;
    // Use the header's viewport bottom, not only offsetHeight. In an iPhone
    // installed PWA the header starts below env(safe-area-inset-top), so its
    // bottom is safe-area + height. Using height alone placed the fixed ticket
    // filters underneath the header, making them look like they scrolled away.
    const set = () => {
      frame = null;
      if (disposed || headers.get(varName) !== el) return;
      const bottom = Math.max(0, el.getBoundingClientRect().bottom);
      if (bottom !== previous) {
        document.documentElement.style.setProperty(varName, `${bottom}px`);
        previous = bottom;
      }
    };
    const schedule = () => { if (frame === null) frame = requestAnimationFrame(set); };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    window.addEventListener("resize", set);
    window.addEventListener("orientationchange", set);
    window.addEventListener("scroll", schedule, { passive: true, capture: true });
    window.visualViewport?.addEventListener("resize", set);
    window.visualViewport?.addEventListener("scroll", schedule);
    return () => {
      disposed = true;
      ro.disconnect();
      if (headers.get(varName) === el) {
        headers.delete(varName);
        document.documentElement.style.removeProperty(varName);
      }
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("resize", set);
      window.removeEventListener("orientationchange", set);
      window.removeEventListener("scroll", schedule, true);
      window.visualViewport?.removeEventListener("resize", set);
      window.visualViewport?.removeEventListener("scroll", schedule);
    };
  }, [varName]);
  return ref;
}
